import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Edit, Trash2, ShoppingCart, Printer, X, Building2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { apiClient } from '../../utils/apiHelper';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PurchaseEntry = () => {
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [banks, setBanks] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [loading, setLoading] = useState(false);
  const [collegeInfo, setCollegeInfo] = useState(null);
  const printRef = useRef();
  const [formData, setFormData] = useState({
    purchase_number: `PUR-${Date.now()}`,
    supplier_id: '',
    supplier_name: '',
    purchase_date: new Date().toISOString().split('T')[0],
    items: '',
    quantity: 1,
    unit_price: 0,
    total_amount: 0,
    paid_amount: 0,
    due_amount: 0,
    payment_mode: 'Credit',
    bank_id: '',
    bank_name: '',
    reference_number: '',
    status: 'Pending',
    remarks: ''
  });

  useEffect(() => {
    fetchPurchases();
    fetchSuppliers();
    fetchBanks();
    loadCollegeInfo();
  }, []);

  const loadCollegeInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/tenants/my-college/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setCollegeInfo({
          ...response.data,
          college_name: response.data.name || response.data.college_name,
        });
      }
    } catch (error) {
      console.error('Error fetching college info:', error);
    }
  };

  const fetchPurchases = async () => {
    try {
      const response = await apiClient.get('/api/accounts/purchases');
      setPurchases(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching purchases:', error);
      setPurchases([]);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await apiClient.get('/api/accounts/suppliers');
      setSuppliers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setSuppliers([]);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await apiClient.get('/api/accounts/banks');
      setBanks(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching banks:', error);
      setBanks([]);
    }
  };

  const handleSupplierChange = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    if (supplier) {
      setFormData({
        ...formData,
        supplier_id: supplierId,
        supplier_name: supplier.supplier_name || supplier.name
      });
    }
  };

  const handleBankChange = (bankId) => {
    const bank = banks.find(b => b.id === bankId);
    if (bank) {
      setFormData({ ...formData, bank_id: bankId, bank_name: bank.bank_name });
    }
  };

  const calculateTotal = (qty, price) => {
    const total = qty * price;
    setFormData(prev => ({
      ...prev,
      quantity: qty,
      unit_price: price,
      total_amount: total,
      due_amount: total - prev.paid_amount
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        ...formData,
        due_amount: formData.total_amount - formData.paid_amount
      };
      
      if (editingPurchase) {
        await apiClient.put(`/api/accounts/purchases/${editingPurchase.id}`, data);
      } else {
        await apiClient.post('/api/accounts/purchases', data);
      }
      alert('Purchase saved successfully!');
      fetchPurchases();
      handleCloseModal();
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to save purchase');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (purchase) => {
    setEditingPurchase(purchase);
    setFormData({
      purchase_number: purchase.purchase_number,
      supplier_id: purchase.supplier_id,
      supplier_name: purchase.supplier_name,
      purchase_date: purchase.purchase_date,
      items: purchase.items || '',
      quantity: purchase.quantity || 1,
      unit_price: purchase.unit_price || 0,
      total_amount: purchase.total_amount,
      paid_amount: purchase.paid_amount || 0,
      due_amount: purchase.due_amount || 0,
      payment_mode: purchase.payment_mode || 'Credit',
      bank_id: purchase.bank_id || '',
      bank_name: purchase.bank_name || '',
      reference_number: purchase.reference_number || '',
      status: purchase.status || 'Pending',
      remarks: purchase.remarks || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this purchase?')) {
      try {
        await apiClient.delete(`/api/accounts/purchases/${id}`);
        fetchPurchases();
      } catch (error) {
        alert('Failed to delete');
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPurchase(null);
    setFormData({
      purchase_number: `PUR-${Date.now()}`,
      supplier_id: '',
      supplier_name: '',
      purchase_date: new Date().toISOString().split('T')[0],
      items: '',
      quantity: 1,
      unit_price: 0,
      total_amount: 0,
      paid_amount: 0,
      due_amount: 0,
      payment_mode: 'Credit',
      bank_id: '',
      bank_name: '',
      reference_number: '',
      status: 'Pending',
      remarks: ''
    });
  };

  const handlePrint = (purchase) => {
    setSelectedPurchase(purchase);
    setIsPrintModalOpen(true);
  };

  const printReceipt = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Purchase Invoice - ${selectedPurchase?.purchase_number}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
            .header h1 { margin: 0; color: #1a56db; }
            .header p { margin: 5px 0; color: #666; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
            .info-box { background: #f8f9fa; padding: 15px; border-radius: 8px; }
            .info-box h3 { margin: 0 0 10px 0; color: #333; font-size: 14px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
            .info-box p { margin: 5px 0; font-size: 13px; }
            .info-box strong { color: #333; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
            th { background: #1a56db; color: white; }
            .totals { margin-top: 20px; }
            .totals table { width: 300px; margin-left: auto; }
            .totals td { border: none; padding: 5px 10px; }
            .totals tr:last-child { font-weight: bold; font-size: 16px; border-top: 2px solid #333; }
            .footer { margin-top: 40px; text-align: center; color: #666; font-size: 12px; }
            .status { display: inline-block; padding: 3px 10px; border-radius: 15px; font-size: 12px; }
            .status-paid { background: #d1fae5; color: #065f46; }
            .status-partial { background: #fef3c7; color: #92400e; }
            .status-pending { background: #fee2e2; color: #991b1b; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Summary
  const totalPurchases = purchases.reduce((sum, p) => sum + (p.total_amount || 0), 0);
  const totalPaid = purchases.reduce((sum, p) => sum + (p.paid_amount || 0), 0);
  const totalDue = purchases.reduce((sum, p) => sum + (p.due_amount || 0), 0);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShoppingCart className="w-6 h-6 text-blue-600" />
            Purchase Entry
          </h1>
          <p className="text-gray-500 text-sm">Record purchases from suppliers</p>
        </div>
        <Button onClick={() => setIsModalOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          New Purchase
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-3">
            <p className="text-xs text-blue-600">Total Purchases</p>
            <p className="text-xl font-bold text-blue-700">{purchases.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-3">
            <p className="text-xs text-purple-600">Total Amount</p>
            <p className="text-xl font-bold text-purple-700">₹{totalPurchases.toLocaleString('en-IN')}</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-3">
            <p className="text-xs text-green-600">Total Paid</p>
            <p className="text-xl font-bold text-green-700">₹{totalPaid.toLocaleString('en-IN')}</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-3">
            <p className="text-xs text-red-600">Total Due</p>
            <p className="text-xl font-bold text-red-700">₹{totalDue.toLocaleString('en-IN')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Purchases Table */}
      <Card>
        <CardContent className="pt-4 overflow-x-auto">
          <table className="w-full border-collapse min-w-[900px]">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3 w-[120px]">Purchase #</th>
                <th className="text-left p-3 w-[100px]">Date</th>
                <th className="text-left p-3 w-[120px]">Supplier</th>
                <th className="text-left p-3 w-[180px]">Items</th>
                <th className="text-right p-3 w-[80px]">Amount</th>
                <th className="text-right p-3 w-[80px]">Paid</th>
                <th className="text-right p-3 w-[80px]">Due</th>
                <th className="text-center p-3 w-[90px]">Mode</th>
                <th className="text-center p-3 w-[70px]">Status</th>
                <th className="text-center p-3 w-[100px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {purchases.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center p-8 text-gray-500">
                    <ShoppingCart className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No purchases recorded yet.</p>
                  </td>
                </tr>
              ) : (
                purchases.map((purchase) => (
                  <tr key={purchase.id} className="border-b hover:bg-gray-50">
                    <td className="p-3 font-medium text-sm">
                      <span className="block truncate max-w-[110px]" title={purchase.purchase_number}>
                        {purchase.purchase_number?.replace('PUR-', '').slice(-8) || purchase.purchase_number}
                      </span>
                    </td>
                    <td className="p-3 text-sm">{purchase.purchase_date}</td>
                    <td className="p-3 text-sm truncate max-w-[120px]" title={purchase.supplier_name}>{purchase.supplier_name}</td>
                    <td className="p-3 text-sm">
                      <span className="block truncate max-w-[170px]" title={purchase.items}>{purchase.items}</span>
                    </td>
                    <td className="p-3 text-right text-sm">₹{purchase.total_amount?.toLocaleString('en-IN')}</td>
                    <td className="p-3 text-right text-sm text-green-600">₹{purchase.paid_amount?.toLocaleString('en-IN')}</td>
                    <td className="p-3 text-right text-sm">
                      {purchase.due_amount > 0 ? (
                        <span className="text-red-600 font-medium">₹{purchase.due_amount?.toLocaleString('en-IN')}</span>
                      ) : (
                        <span className="text-green-600">₹0</span>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        purchase.payment_mode === 'Cash' ? 'bg-green-100 text-green-800' :
                        purchase.payment_mode === 'Bank Transfer' ? 'bg-blue-100 text-blue-800' :
                        purchase.payment_mode === 'UPI' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {purchase.payment_mode === 'Bank Transfer' ? 'Bank' : purchase.payment_mode || 'Credit'}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        purchase.status === 'Paid' ? 'bg-green-100 text-green-800' :
                        purchase.status === 'Partial' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {purchase.status}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex gap-1 justify-center">
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => handlePrint(purchase)} title="Print Invoice">
                          <Printer className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => handleEdit(purchase)} title="Edit">
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="destructive" className="h-7 w-7 p-0" onClick={() => handleDelete(purchase.id)} title="Delete">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Add/Edit Purchase Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPurchase ? 'Edit Purchase' : 'New Purchase Entry'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Purchase Number</Label>
                <Input value={formData.purchase_number} readOnly className="bg-gray-100" />
              </div>
              <div>
                <Label>Purchase Date *</Label>
                <Input 
                  type="date" 
                  required 
                  value={formData.purchase_date} 
                  onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })} 
                />
              </div>
            </div>

            <div>
              <Label>Select Supplier *</Label>
              <Select value={formData.supplier_id} onValueChange={handleSupplierChange}>
                <SelectTrigger>
                  <SelectValue placeholder="-- Select Supplier --" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.length === 0 ? (
                    <SelectItem value="__none__" disabled>No suppliers found. Add a supplier first.</SelectItem>
                  ) : (
                    suppliers.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.supplier_name || s.name} - {s.company_name || ''}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Items / Description *</Label>
              <Textarea 
                required
                value={formData.items} 
                onChange={(e) => setFormData({ ...formData, items: e.target.value })}
                placeholder="Enter items purchased (e.g., Notebooks - 100pcs, Pens - 500pcs)"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Quantity</Label>
                <Input 
                  type="number" 
                  min="1"
                  value={formData.quantity} 
                  onChange={(e) => calculateTotal(parseInt(e.target.value) || 1, formData.unit_price)} 
                />
              </div>
              <div>
                <Label>Unit Price (₹)</Label>
                <Input 
                  type="number" 
                  min="0"
                  value={formData.unit_price} 
                  onChange={(e) => calculateTotal(formData.quantity, parseFloat(e.target.value) || 0)} 
                />
              </div>
              <div>
                <Label>Total Amount (₹) *</Label>
                <Input 
                  type="number" 
                  required
                  value={formData.total_amount} 
                  onChange={(e) => {
                    const total = parseFloat(e.target.value) || 0;
                    setFormData({ 
                      ...formData, 
                      total_amount: total,
                      due_amount: total - formData.paid_amount
                    });
                  }} 
                  className="font-bold"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Payment Mode *</Label>
                <Select value={formData.payment_mode} onValueChange={(value) => setFormData({ ...formData, payment_mode: value, bank_id: value === 'Cash' || value === 'Credit' ? '' : formData.bank_id })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Credit">Credit (Pay Later)</SelectItem>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                    <SelectItem value="UPI">UPI</SelectItem>
                    <SelectItem value="Cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Advance Paid (₹)</Label>
                <Input 
                  type="number" 
                  min="0"
                  value={formData.paid_amount} 
                  onChange={(e) => {
                    const paid = parseFloat(e.target.value) || 0;
                    setFormData({ 
                      ...formData, 
                      paid_amount: paid,
                      due_amount: formData.total_amount - paid,
                      status: paid >= formData.total_amount ? 'Paid' : paid > 0 ? 'Partial' : 'Pending'
                    });
                  }} 
                />
              </div>
            </div>

            {/* Bank Selection - Show only for bank payments */}
            {(formData.payment_mode === 'Bank Transfer' || formData.payment_mode === 'UPI' || formData.payment_mode === 'Cheque') && (
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <Label className="text-blue-700">
                  <Building2 className="w-4 h-4 inline mr-1" />
                  Select Bank Account
                </Label>
                <Select value={formData.bank_id} onValueChange={handleBankChange}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select bank account" />
                  </SelectTrigger>
                  <SelectContent>
                    {banks.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.bank_name} - {b.account_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="mt-2">
                  <Label>Reference Number</Label>
                  <Input 
                    value={formData.reference_number} 
                    onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                    placeholder="Transaction/Cheque Number"
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Due Amount (₹)</Label>
                <Input 
                  type="number" 
                  value={formData.due_amount} 
                  readOnly 
                  className={`font-bold ${formData.due_amount > 0 ? 'text-red-600 bg-red-50' : 'text-green-600 bg-green-50'}`}
                />
              </div>
              <div>
                <Label>Remarks</Label>
                <Input 
                  value={formData.remarks} 
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  placeholder="Any additional notes"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseModal}>Cancel</Button>
              <Button type="submit" disabled={loading || !formData.supplier_id}>
                {loading ? 'Saving...' : 'Save Purchase'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Print Invoice Modal */}
      <Dialog open={isPrintModalOpen} onOpenChange={setIsPrintModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Purchase Invoice</span>
              <div className="flex gap-2">
                <Button onClick={printReceipt} className="bg-blue-600 hover:bg-blue-700">
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
                <Button variant="outline" onClick={() => setIsPrintModalOpen(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {selectedPurchase && (
            <div ref={printRef} className="p-4">
              {/* Header */}
              <div className="header text-center border-b-2 border-gray-800 pb-4 mb-6">
                <h1 className="text-2xl font-bold text-blue-700">PURCHASE INVOICE</h1>
                <p className="text-gray-600">{collegeInfo?.college_name || collegeInfo?.name || 'Institution'}</p>
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-gray-700 border-b pb-2 mb-3">Invoice Details</h3>
                  <p><strong>Invoice No:</strong> {selectedPurchase.purchase_number}</p>
                  <p><strong>Date:</strong> {selectedPurchase.purchase_date}</p>
                  <p><strong>Payment Mode:</strong> {selectedPurchase.payment_mode || 'Credit'}</p>
                  {selectedPurchase.bank_name && <p><strong>Bank:</strong> {selectedPurchase.bank_name}</p>}
                  {selectedPurchase.reference_number && <p><strong>Ref No:</strong> {selectedPurchase.reference_number}</p>}
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-gray-700 border-b pb-2 mb-3">Supplier Details</h3>
                  <p><strong>Name:</strong> {selectedPurchase.supplier_name}</p>
                  <p><strong>Status:</strong> 
                    <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                      selectedPurchase.status === 'Paid' ? 'bg-green-100 text-green-800' :
                      selectedPurchase.status === 'Partial' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {selectedPurchase.status}
                    </span>
                  </p>
                </div>
              </div>

              {/* Items Table */}
              <table className="w-full border-collapse mb-6">
                <thead>
                  <tr className="bg-blue-600 text-white">
                    <th className="border p-3 text-left">Description</th>
                    <th className="border p-3 text-center">Qty</th>
                    <th className="border p-3 text-right">Unit Price</th>
                    <th className="border p-3 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border p-3">{selectedPurchase.items}</td>
                    <td className="border p-3 text-center">{selectedPurchase.quantity || 1}</td>
                    <td className="border p-3 text-right">₹{selectedPurchase.unit_price?.toLocaleString('en-IN') || 0}</td>
                    <td className="border p-3 text-right font-bold">₹{selectedPurchase.total_amount?.toLocaleString('en-IN')}</td>
                  </tr>
                </tbody>
              </table>

              {/* Totals */}
              <div className="flex justify-end">
                <table className="w-72">
                  <tbody>
                    <tr>
                      <td className="p-2 text-right">Total Amount:</td>
                      <td className="p-2 text-right font-medium">₹{selectedPurchase.total_amount?.toLocaleString('en-IN')}</td>
                    </tr>
                    <tr>
                      <td className="p-2 text-right">Paid Amount:</td>
                      <td className="p-2 text-right text-green-600">₹{selectedPurchase.paid_amount?.toLocaleString('en-IN')}</td>
                    </tr>
                    <tr className="border-t-2 border-gray-800">
                      <td className="p-2 text-right font-bold">Due Amount:</td>
                      <td className={`p-2 text-right font-bold text-lg ${selectedPurchase.due_amount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        ₹{selectedPurchase.due_amount?.toLocaleString('en-IN')}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Remarks */}
              {selectedPurchase.remarks && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <strong>Remarks:</strong> {selectedPurchase.remarks}
                </div>
              )}

              {/* Footer */}
              <div className="mt-8 pt-4 border-t text-center text-gray-500 text-sm">
                <p>This is a computer generated invoice.</p>
                <p>Generated on: {new Date().toLocaleString('en-IN')}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseEntry;
